import { Check } from 'lucide-react'
import { DELIVERY_STATUS_VARIANT } from '../lib/delivery-status-variant'
import type { ColumnDef } from '@tanstack/react-table'
import type { DeliveryListItem } from '@/api/endpoints/inventory-deliveries'
import { Badge } from '@/components/ui/badge'

/**
 * `expectedDate` is a plain calendar day (yyyy-MM-dd) decided in the store's timezone.
 * `new Date('2026-09-02')` would parse it as UTC midnight and render the previous day for any
 * viewer behind UTC, so the parts are turned into a local date instead.
 */
export function formatIsoDate(value?: string | null): string {
  if (!value) return '–'
  const parts = value.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return value
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString()
}

export function formatTimestamp(value?: string | null): string {
  if (!value) return '–'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

/**
 * The list endpoint pages and orders server-side and takes no sort parameter, so client sorting
 * would silently reorder one page only — every column is therefore unsortable.
 */
export function buildDeliveryColumns(
  t: (key: string, options?: Record<string, unknown>) => string,
  options: { compact?: boolean } = {},
): Array<ColumnDef<DeliveryListItem>> {
  const columns: Array<ColumnDef<DeliveryListItem>> = [
    {
      id: 'status',
      header: t('list.column.status'),
      size: 210,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant={DELIVERY_STATUS_VARIANT[row.original.status]}>
            {t(`status.${row.original.status}`)}
          </Badge>
          {row.original.overdue && (
            <Badge variant="destructive">{t('status.overdue')}</Badge>
          )}
          {/* Provenance rides in the status cell rather than in a column of its own: it is the
              one cluster that survives the phone layout, and a badge on every manual row would
              say nothing (CHG-2026-09-webbill-delivery-import). */}
          {row.original.source !== 'MANUAL' && (
            <Badge variant="outline">
              {t(`source.${row.original.source}`, {
                defaultValue: row.original.source,
              })}
            </Badge>
          )}
        </div>
      ),
      meta: { label: t('list.column.status') },
    },
    {
      accessorKey: 'expectedDate',
      header: t('list.column.expectedDate'),
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <span>{formatIsoDate(row.original.expectedDate)}</span>
      ),
      meta: { label: t('list.column.expectedDate') },
    },
    {
      accessorKey: 'supplier',
      header: t('list.column.supplier'),
      size: 180,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-medium">{row.original.supplier ?? '–'}</span>
      ),
      meta: { label: t('list.column.supplier') },
    },
    {
      accessorKey: 'deliveryNoteRef',
      header: t('list.column.deliveryNoteRef'),
      size: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original.deliveryNoteRef ?? '–'}
        </span>
      ),
      meta: { label: t('list.column.deliveryNoteRef') },
    },
    {
      accessorKey: 'lineCount',
      header: t('list.column.lines'),
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">
          {row.original.lineCount}
        </span>
      ),
      meta: { label: t('list.column.lines') },
    },
    {
      id: 'countsForPreorders',
      header: t('list.column.countsForPreorders'),
      size: 150,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.countsForPreorders ? (
          <Check
            className="h-4 w-4 text-muted-foreground"
            aria-label={t('list.column.countsForPreorders')}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      meta: { label: t('list.column.countsForPreorders') },
    },
    {
      accessorKey: 'createdBy',
      header: t('list.column.createdBy'),
      size: 160,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.createdBy ?? '–'}
        </span>
      ),
      meta: { label: t('list.column.createdBy') },
    },
    {
      accessorKey: 'bookedAt',
      header: t('list.column.bookedAt'),
      size: 170,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatTimestamp(row.original.bookedAt)}
        </span>
      ),
      meta: { label: t('list.column.bookedAt') },
    },
  ]

  if (!options.compact) return columns
  // Phone: status, day and supplier are what a staff member needs to find the right document.
  const dropped = new Set(['lineCount', 'createdBy', 'bookedAt'])
  return columns.filter(
    (c) => !dropped.has((c as { accessorKey?: string }).accessorKey ?? c.id!),
  )
}
